import React from "react";
import {
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDraggable } from "@dnd-kit/core";

function DraggableItem({ id, label, data }) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    data
  });

  return (
    <Button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        justifyContent: "flex-start",
        textTransform: "none"
      }}
    >
      {label}
    </Button>
  );
}

function PaletteSection({
  title,
  children,
  defaultExpanded = true,
  maxHeight = "30vh"
}) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">
          {title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          maxHeight,
          overflowY: "auto",
          p: 1,
          "&::-webkit-scrollbar": {
            width: 8
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bdbdbd",
            borderRadius: 4
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#9e9e9e"
          }
        }}
      >
        <Stack spacing={1}>
          {children}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function FieldPalette({
  fieldGroups = {},
  designs = [],
  templates = [],
  selectedDesignId = "",
  selectedOwnership = "",
  onNew,
  onOpenDesign,
  onOpenTemplate,
  reportType = "FORM",
  readOnly = false
}) {
  const elementItems = [
    { id: "element-label", label: "Label", data: { type: "label", text: "Label" } },
    { id: "element-text", label: "Text", data: { type: "typography", text: "Text" } },
    { id: "element-line", label: "Line", data: { type: "line" } },
    { id: "element-rectangle", label: "Rectangle", data: { type: "rectangle" } },
    { id: "element-image", label: "Image", data: { type: "image" } },
    { id: "element-table", label: "Table", data: { type: "table" } }
  ];

  const tableItems = [
    ...(reportType === "COLUMNAR" ? [{
      id: "columnar-table",
      label: "Columnar Table",
      data: {
        type: "columnarTable",
        text: "Columnar Table"
      }
    }] : []),
    ...(reportType === "FORM" ? [
    {
      id: "transaction-detail-table",
      label: "Transaction Detail Table",
      data: {
        type: "transactionDetailTable",
        text: "Transaction Detail Table"
      }
    }] : []),
    {
      id: "page-header-band",
      label: "Page Header",
      data: {
        type: "pageHeaderBand",
        text: "Page Header"
      }
    },
    {
      id: "page-footer-band",
      label: "Page Footer",
      data: {
        type: "pageFooterBand",
        text: "Page Footer"
      }
    },
    {
      id: "group-header-band",
      label: "Group Header",
      data: {
        type: "groupHeaderBand",
        text: "Group Header"
      }
    },
    {
      id: "group-footer-band",
      label: "Group Footer",
      data: {
        type: "groupFooterBand",
        text: "Group Footer"
      }
    },
    {
      id: "summary-band",
      label: "Summary Band",
      data: {
        type: "summaryBand",
        text: "Summary Band"
      }
    }
  ];

  const hiddenGroups = [
    "Transaction Details",
    "Transaction Details Fields"
  ];

  const fieldGroupEntries = Object.entries(fieldGroups || {}).filter(
    ([groupTitle]) => !hiddenGroups.includes(groupTitle)
  );

  return (
    <Paper sx={{ p: 2, width: "100%" }}>
      <Stack spacing={1}>
        <Button variant="contained" size="small" onClick={onNew}>
          New
        </Button>

        <TextField
          select
          label="System Templates"
          size="small"
          value={selectedOwnership === "SYSTEM" ? selectedDesignId || "" : ""}
          onChange={e => onOpenTemplate && onOpenTemplate(e.target.value)}
        >
          <MenuItem value="">Select template</MenuItem>
          {templates.map(template => (
            <MenuItem key={template.sReportID} value={template.sReportID}>
              {template.sReportName || template.sReportTitle}
              {template.nTemplateVersion ? ` (v${template.nTemplateVersion})` : ""}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="My Reports"
          size="small"
          value={selectedOwnership === "USER" ? selectedDesignId || "" : ""}
          onChange={e => onOpenDesign && onOpenDesign(e.target.value)}
        >
          <MenuItem value="">Select report</MenuItem>
          {designs.map(design => (
            <MenuItem
              key={design.sReportID}
              value={design.sReportID}
            >
              {design.sReportName || design.sReportTitle}
            </MenuItem>
          ))}
        </TextField>

        {readOnly && (
          <Typography variant="caption" color="text.secondary">
            Source authoring is disabled. Clone this template to customize it.
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <PaletteSection title="ELEMENTS" maxHeight={180}>
        {elementItems.map(item => (
          <DraggableItem
            key={item.id}
            id={item.id}
            label={item.label}
            data={item.data}
          />
        ))}
      </PaletteSection>

      <Divider sx={{ my: 2 }} />

        <PaletteSection title="TABLES" maxHeight={120}>
          {tableItems.map(item => (
            <DraggableItem
              key={item.id}
              id={item.id}
              label={item.label}
              data={item.data}
            />
          ))}
        </PaletteSection>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        FIELDS
      </Typography>

      {fieldGroupEntries.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          Create or open a report to load available fields.
        </Typography>
      )}

      <PaletteSection
        title="Page Fields"
        defaultExpanded={false}
      >
        <DraggableItem
          id="field-page-number"
          label="Page Number"
          data={{
            type: "field",
            label: "Page Number",
            fieldPath: "page.number",
            sourceSection: "page",
            fieldType: "NUMBER",
            dataType: "NUMBER",
            format: "0"
          }}
        />

        <DraggableItem
          id="field-page-total"
          label="Total Pages"
          data={{
            type: "field",
            label: "Total Pages",
            fieldPath: "page.total",
            sourceSection: "page",
            fieldType: "NUMBER",
            dataType: "NUMBER",
            format: "0"
          }}
        />
      </PaletteSection>

      <PaletteSection
        title="Group Fields"
        defaultExpanded={false}
      >
        <DraggableItem
          id="field-group-value"
          label="Group Value"
          data={{
            type: "field",
            label: "Group Value",
            fieldPath: "group.value",
            sourceSection: "group",
            fieldType: "TEXT",
            dataType: "TEXT",
            format: ""
          }}
        />
      </PaletteSection>

      {fieldGroupEntries.map(([groupTitle, fields]) => (
      <PaletteSection
        key={groupTitle}
        title={groupTitle}
        defaultExpanded={
          groupTitle === "Company Information" ||
          groupTitle === "Main Record" ||
          groupTitle === "Transaction Summary"
        }
      >
        {groupTitle === "Transaction Summary" && (
          <>
            <DraggableItem
              id="summary-tax-list"
              label="Tax List"
              data={{
                type: "summaryTaxList",
                label: "Tax List",
                fieldPath: "summary.summ_tax",
                sourceSection: "summary",

                taxValueMode: "CALCULATED_TAX",

                rowHeight: 18,
                valueWidth: 80,
                showTaxCode: false,
                showTaxName: true
              }}
            />

            <DraggableItem
              id="summary-withholding-list"
              label="Withholding Tax List"
              data={{
                type: "summaryWithholdingList",
                label: "Withholding Tax List",
                fieldPath: "summary.summ_withholding",
                sourceSection: "summary"
              }}
            />
          </>
        )}

        {(fields || [])
          .filter(field => {
            /*
            * Repeating tax rows should be rendered through Tax List,
            * not through single-value tax fields.
            */
            if (groupTitle !== "Transaction Summary") {
              return true;
            }

            const fieldPath = String(field.fieldPath || "");

            return (
              fieldPath !== "summary.summ_tax" &&
              fieldPath !== "summary.summ_withholding" &&
              !fieldPath.startsWith("summary.summ_tax.") &&
              !fieldPath.startsWith("summary.summ_withholding.")
            );
          })
          .map(field => (
            <DraggableItem
              key={field.fieldPath}
              id={`field-${field.fieldPath}`}
              label={field.label}
              data={{
                type: "field",
                label: field.label,
                fieldPath: field.fieldPath,
                sourceSection:
                  field.sourceSection ||
                  field.fieldPath?.split(".")[0],
                fieldType: field.fieldType,
                dataType: field.dataType,
                format: field.format || ""
              }}
            />
          ))}
      </PaletteSection>
))}
    </Paper>
  );
}
